import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { apiGet, apiPatch, formatEuro } from "../../lib/api";
import {
  STATUS_LABELS,
  STATUS_OPTIONS,
  buildAdminAuthHeaders,
  countOrderItems,
  formatDateTime,
  getCityFromOrder,
  isUnauthorizedAdminError,
  shortOrderId
} from "./admin-utils";
import "../Shop.css";

const AdminOrdersPage = () => {
  const { adminToken, handleUnauthorized } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [statusDraftById, setStatusDraftById] = useState({});
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [isOrdersRefreshing, setIsOrdersRefreshing] = useState(false);
  const [savingOrderId, setSavingOrderId] = useState("");
  const [error, setError] = useState("");
  const [statusNotice, setStatusNotice] = useState("");

  const formatNotificationNotice = (notification) => {
    if (!notification || typeof notification !== "object") {
      return "Stato ordine aggiornato.";
    }

    if (notification.outcome === "sent") {
      return `Stato aggiornato. Email inviata a ${notification.recipient || "cliente"}.`;
    }

    if (notification.outcome === "duplicate") {
      return "Stato aggiornato. Email non reinviata (gia' notificato per questo stato).";
    }

    if (notification.outcome === "missing_customer_email") {
      return "Stato aggiornato. Email cliente assente: notifica saltata.";
    }

    if (notification.outcome === "status_not_supported") {
      return "Stato aggiornato. Nessuna email prevista per questo stato.";
    }

    if (notification.outcome === "email_sender_not_configured") {
      return "Stato aggiornato. Mittente email non configurato in backend.";
    }

    if (notification.outcome === "provider_error") {
      return "Stato aggiornato, ma invio email non riuscito (vedi log backend).";
    }

    return "Stato ordine aggiornato.";
  };

  const loadOrders = useCallback(
    async ({ refreshOnly = false } = {}) => {
      if (!adminToken) {
        return;
      }

      setError("");
      setStatusNotice("");
      if (refreshOnly) {
        setIsOrdersRefreshing(true);
      } else {
        setIsOrdersLoading(true);
      }

      try {
        const payload = await apiGet("/api/admin/orders?limit=100", {
          headers: buildAdminAuthHeaders(adminToken)
        });
        const nextOrders = Array.isArray(payload?.orders) ? payload.orders : [];
        setOrders(nextOrders);

        const nextDrafts = {};
        for (const order of nextOrders) {
          nextDrafts[order.id] = order.status;
        }
        setStatusDraftById(nextDrafts);
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : "Errore caricamento ordini";
        if (isUnauthorizedAdminError(loadError)) {
          handleUnauthorized();
          return;
        }
        setError(message);
      } finally {
        if (refreshOnly) {
          setIsOrdersRefreshing(false);
        } else {
          setIsOrdersLoading(false);
        }
      }
    },
    [adminToken, handleUnauthorized]
  );

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleStatusChange = (orderId, value) => {
    setStatusDraftById((current) => ({
      ...current,
      [orderId]: value
    }));
  };

  const handleStatusSave = async (orderId) => {
    const nextStatus = statusDraftById[orderId];
    if (!STATUS_OPTIONS.includes(nextStatus)) {
      setError("Stato ordine non valido.");
      return;
    }

    setSavingOrderId(orderId);
    setError("");
    setStatusNotice("");

    try {
      const payload = await apiPatch(
        `/api/admin/orders/${orderId}/status`,
        { status: nextStatus },
        {
          headers: buildAdminAuthHeaders(adminToken)
        }
      );

      setOrders((currentOrders) =>
        currentOrders.map((order) => (order.id === orderId ? { ...order, status: nextStatus } : order))
      );
      setStatusNotice(formatNotificationNotice(payload?.notification));
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Errore aggiornamento stato";
      if (isUnauthorizedAdminError(saveError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setSavingOrderId("");
    }
  };

  return (
    <section className="admin-section-block">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Ordini</h3>
        <button
          className="cart-clear-button"
          type="button"
          onClick={() => loadOrders({ refreshOnly: true })}
          disabled={isOrdersRefreshing || isOrdersLoading}
        >
          {isOrdersRefreshing ? "Aggiornamento..." : "Ricarica ordini"}
        </button>
      </div>

      {error && <p className="shop-state shop-error">Errore: {error}</p>}
      {!error && statusNotice && <p className="shop-state">{statusNotice}</p>}
      {isOrdersLoading && <p className="shop-state">Caricamento ordini...</p>}
      {!isOrdersLoading && orders.length === 0 && <p className="shop-state">Nessun ordine disponibile.</p>}

      {!isOrdersLoading && orders.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-orders-table">
            <thead>
              <tr>
                <th>Ordine</th>
                <th>Cliente</th>
                <th>Totale</th>
                <th>Stato</th>
                <th>Azione</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const isSaving = savingOrderId === order.id;
                const draftStatus = statusDraftById[order.id] || order.status;
                const customerName = order.customer_name || order.shipping_name || order.customer_email || "-";
                const city = getCityFromOrder(order);
                const itemCount = countOrderItems(order.items);

                return (
                  <tr key={order.id}>
                    <td>
                      <strong>{shortOrderId(order.id)}</strong>
                      <div className="admin-row-meta">{formatDateTime(order.created_at)}</div>
                      <div className="admin-row-meta">{itemCount} articoli</div>
                    </td>
                    <td>
                      <strong>{customerName}</strong>
                      <div className="admin-row-meta">{order.customer_email || "-"}</div>
                      <div className="admin-row-meta">{city}</div>
                    </td>
                    <td>{formatEuro(order.total_cents)}</td>
                    <td>
                      <select
                        className="admin-status-select"
                        value={draftStatus}
                        onChange={(event) => handleStatusChange(order.id, event.target.value)}
                      >
                        {STATUS_OPTIONS.map((statusCode) => (
                          <option key={statusCode} value={statusCode}>
                            {STATUS_LABELS[statusCode]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="shop-add-button admin-save-button"
                        onClick={() => handleStatusSave(order.id)}
                        disabled={isSaving}
                      >
                        {isSaving ? "Salvataggio..." : "Salva"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminOrdersPage;
